// frontend/src/components/TorusSpring3D.jsx
import React, { useRef, useState, useEffect } from "react";
import { creerLecteurContinu } from "../utils/continuationPlayback";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { Line } from "@react-three/drei";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import useTorusSpringVisualizer from "../hooks/useTorusSpringVisualizer";

export default function TorusSpring3D({
  running = true,
  chaos = false,
  glow = true,
  spiralSpeedPct = 50,
  flashRatePct = 30,
  bloomIntensity = 1.2,
  bloomRadius = 0.6,
  bloomThreshold = 0.0,
  onUIEntropyUpdate = () => { },
  onDynEntropyUpdate = () => { },
}) {
  const groupRef = useRef();
  // ⚠️ Garde sur `torus` : voir le commentaire jumeau dans CentrifugeLaser3D.
  // Un composant de rendu ne doit jamais planter sur une donnée incomplète.
  const scene = useTorusSpringVisualizer({});
  const { spheres, springs } = scene;
  const torus = scene.torus ?? { major_radius: 8, minor_radius: 3 };
  const [angle, setAngle] = useState(0);
  const [flashPos, setFlashPos] = useState(0);
  const lastFetchRef = useRef(0);

  // ═══════════════════════════════════════════════════════════════════════
  // LE BACKEND POSSÈDE LE MOUVEMENT — docs/DOCTRINE_GEOMETRIE_CANONIQUE.md
  // Le front rejoue les frames de `/animate`, produites par le vrai moteur
  // (forces de ressorts, gravité, résistance de l'air, masses).
  // ═══════════════════════════════════════════════════════════════════════
  const lecteurRef = useRef(null);
  // Références aux lignes de ressort, pour les faire SUIVRE le rejeu.
  const ressortRefs = useRef([]);
  const playheadRef = useRef(0);
  const snapshotRef = useRef({ positions: [], velocities: [] });

  // ⚠️ LECTURE CONTINUE — le rejeu d'un segment unique téléportait la scène à
  // la couture : 147x le pas typique, le deuxième pire des huit modules.
  // Le curseur pilote l'AMPLITUDE d'oscillation du moteur, pas le rejeu.
  useEffect(() => {
    const lecteur = creerLecteurContinu({
      url: "/api/geometry/torus_spring/animate",
      // ⚠️ `spiralSpeedPct` pilotait la CADENCE DE LECTURE
      // (`playhead += delta * (0.5 + speedFactor * 2.0)`). Il n'y contaminait
      // pas la métrique — les vitesses sont dérivées du pas de FRAME, pas du
      // pas réel — mais un curseur appliqué au rejeu reste vidé de son sens
      // physique. Il règle désormais le pas d'intégration servi : plus de
      // temps simulé par frame, donc un système réellement plus rapide.
      parametres: () => ({
        steps: 240,
        dt: 0.008 + (spiralSpeedPct / 100) * 0.024,   // 0..100 % -> 0,008..0,032
        oscillation: chaos ? 1.5 : 0.3,
      }),
    });
    lecteurRef.current = lecteur;
    lecteur.demarrer();
    return () => {
      lecteur.arreter();
      lecteurRef.current = null;
    };
  }, [chaos, spiralSpeedPct]);

  // Animation frame
  useFrame((_, delta) => {
    if (!running || !groupRef.current) return;
    const speedFactor = spiralSpeedPct / 100;

    // Rotation globale
    setAngle((prev) => prev + delta * (0.5 + speedFactor * 2.0));
    groupRef.current.rotation.y = angle * 0.5;
    groupRef.current.rotation.x = angle * 0.2;

    if (chaos) {
      groupRef.current.rotation.z = Math.sin(angle * 0.7) * 0.5;
      groupRef.current.position.x = Math.sin(angle * 0.3) * 0.5;
    } else {
      groupRef.current.rotation.z = 0;
      groupRef.current.position.x = 0;
    }

    // Flash propagation
    setFlashPos((prev) => (prev + delta * (flashRatePct / 10)) % Math.max(1, spheres.length));

    // --- Rejeu des frames backend : positions et vitesses RÉELLES ---
    const lecteur = lecteurRef.current;
    const vue = lecteur ? lecteur.lire(playheadRef.current) : null;
    if (vue) {
      playheadRef.current += delta;
      if (vue.bascule || vue.gel) playheadRef.current = 0;
      const al = vue.alpha;
      const fr = { dt: vue.dt };
      const A = vue.a.spheres;
      const B = vue.b.spheres;

      const positions = [];
      const velocities = [];
      for (let i = 0; i < A.length && i < B.length; i++) {
        const a = A[i].position, b = B[i].position;
        const p = [
          a[0] + (b[0] - a[0]) * al,
          a[1] + (b[1] - a[1]) * al,
          a[2] + (b[2] - a[2]) * al,
        ];
        positions.push(p);
        // Vitesse MESURÉE : différence entre deux frames, sur leur pas de temps.
        velocities.push([
          (b[0] - a[0]) / fr.dt,
          (b[1] - a[1]) / fr.dt,
          (b[2] - a[2]) / fr.dt,
        ]);
      }
      snapshotRef.current = { positions, velocities };

      // ⚠️ LES RESSORTS SUIVENT LES SPHÈRES — correction du 2026-07-29.
      //
      // Les lignes étaient construites à partir de `spheres[i].position`,
      // c'est-à-dire les positions de `/initial`, qui ne bougent JAMAIS. Les
      // sphères étaient déplacées par le rejeu, les ressorts restaient figés
      // dans leur configuration de départ : une cage de ficelles immobile dont
      // les sphères s'éloignaient. C'est ce que la vérification navigateur a vu
      // — « tout est dehors », « deux ficelles dans le fil de fer ».
      //
      // On met à jour la géométrie de chaque ligne plutôt que de la
      // reconstruire : recréer 27 composants par frame coûterait cher pour un
      // contenu qui ne change que de valeur.
      springs.forEach((ressort, idx) => {
        const ligne = ressortRefs.current[idx];
        const p1 = positions[ressort.sphere1];
        const p2 = positions[ressort.sphere2];
        if (ligne && ligne.geometry && p1 && p2) {
          ligne.geometry.setPositions([...p1, ...p2]);
        }
      });

      // Application aux meshes rendus.
      //
      // ⚠️ DÉCALAGE D'INDICE CORRIGÉ le 2026-07-29. Cette boucle parcourait
      // `children` depuis 0 — or le PREMIER enfant du groupe est le tore
      // filaire, pas une sphère. Le tore recevait donc la position de la
      // sphère 0, et chaque sphère `i` celle de la sphère `i-1`. Le repère
      // torique se déplaçait, et l'objet affiché ne correspondait à AUCUN des
      // instantanés mesurés.
      //
      // Le banc de conformité ne pouvait pas le voir : il reconstruit le
      // snapshot depuis les frames de `/animate`, pas depuis la scène rendue.
      // C'est un angle mort assumé du banc — il atteste la chaîne de mesure,
      // pas l'affectation des positions aux objets.
      //
      // On repère les sphères par leur nom plutôt que par leur rang : un rang
      // dépend de l'ordre du JSX, un nom survit à une insertion.
      groupRef.current.children.forEach((child) => {
        const i = child.userData?.sphereIndex;
        if (i !== undefined && positions[i]) {
          child.position.set(positions[i][0], positions[i][1], positions[i][2]);
        }
      });
    }

    // === Entropie Dynamique temps réel (Throttled) ===
    const now = Date.now();
    if (now - lastFetchRef.current < 200) return; // Max 5 req/s
    lastFetchRef.current = now;

    // ⚠️ Les positions et vitesses transmises sont MESURÉES, plus fabriquées.
    // Avant : `window.crypto.getRandomValues()` générait les vitesses envoyées
    // à `/dyn_entropy` comme si elles avaient été observées — un aléatoire
    // cryptographique reste un aléatoire, pas une mesure. Et les positions
    // transmises étaient STATIQUES : seul le groupe tournait, les positions
    // locales des sphères ne bougeaient jamais.
    // Voir docs/DOCTRINE_ENTROPIE_PHYSIQUE.md.
    const snap = snapshotRef.current;
    if (!snap || !snap.positions.length) return;

    try {
      // Le corps ne porte que la SCÈNE : les curseurs agissent sur le moteur
      // (paramètres de `/animate`), jamais sur la métrique — règle 2.
      fetch("/api/geometry/torus_spring/dyn_entropy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          positions: snap.positions,
          velocities: snap.velocities,
        }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (typeof data.entropy_dyn === "number") {
            onDynEntropyUpdate(data.entropy_dyn);
          }
        })
        .catch((err) => console.error("Erreur dyn_entropy TorusSpring:", err));
    } catch (err) {
      console.error("Erreur capture dyn_entropy temps réel:", err);
    }
  });

  // --- Entropie UI ---
  useEffect(() => {
    let active = true;
    async function fetchUIEntropy() {
      try {
        const res = await fetch("/api/geometry/torus_spring/entropy", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            chaos,
            glow,
            spiral_speed: spiralSpeedPct / 100,
            flash_rate: flashRatePct / 100,
            bloom_intensity: bloomIntensity,
            bloom_radius: bloomRadius,
            bloom_threshold: bloomThreshold,
          }),
        });
        const data = await res.json();
        if (active && typeof data.entropy === "number") {
          onUIEntropyUpdate(data.entropy);
        }
      } catch (err) {
        console.error("Erreur fetch entropie UI TorusSpring:", err);
      }
    }
    fetchUIEntropy();


  }, [chaos, glow, spiralSpeedPct, flashRatePct, bloomIntensity, bloomRadius, bloomThreshold, onUIEntropyUpdate]);

  return (
    <>
      <group ref={groupRef}>
        {/* Tore filaire */}
        <mesh>
          <torusGeometry args={[torus.major_radius, torus.minor_radius, 16, 64]} />
          <meshBasicMaterial color={0x333333} wireframe transparent opacity={0.2} />
        </mesh>

        {/* Sphères */}
        {spheres.map((sphere, i) => {
          // ⚠️ La position vient de `/initial`, elle n'est plus RECALCULÉE ici.
          //
          // Ce bloc plaçait les sphères sur un cercle dérivé du tore, avec un
          // `angleOffset` fonction de `angle` — lequel change à chaque frame.
          // React réappliquait donc cette position à chaque rendu, écrasant
          // celle que le rejeu venait d'écrire : deux placements concurrents se
          // disputaient le même objet, et ce qui était affiché ne correspondait
          // à aucun instantané mesuré.
          //
          // La position initiale est désormais canonique, et le rejeu la fait
          // vivre ensuite (docs/DOCTRINE_GEOMETRIE_CANONIQUE.md).
          const distToFlash = Math.abs(i - flashPos);
          const isFlashing = distToFlash < 1.5;

          const baseColor = new THREE.Color(...sphere.color);
          const emissiveIntensity = isFlashing ? 2.5 : glow ? 0.3 : 0;

          return (
            <mesh
              key={i}
              position={sphere.position}
              userData={{ sphereIndex: i }}
            >
              <sphereGeometry args={[sphere.radius, 16, 16]} />
              <meshStandardMaterial
                color={baseColor}
                emissive={baseColor}
                emissiveIntensity={emissiveIntensity}
              />
            </mesh>
          );
        })}

        {/* Ressorts */}
        {springs.map((spring, idx) => {
          const s1 = spheres[spring.sphere1];
          const s2 = spheres[spring.sphere2];
          return (
            <Line
              key={idx}
              ref={(el) => (ressortRefs.current[idx] = el)}
              points={[new THREE.Vector3(...s1.position), new THREE.Vector3(...s2.position)]}
              color={new THREE.Color(...spring.color)}
              lineWidth={2}
              transparent
              opacity={0.8}
            />
          );
        })}
      </group>

      <EffectComposer>
        <Bloom intensity={bloomIntensity} radius={bloomRadius} threshold={bloomThreshold} />
      </EffectComposer>
    </>
  );
}
